import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { oneKlicks } from "../SDK/oneclick";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState({
        isAuthenticated: false,
        isLoading: true,
        error: null
    });
    const isLoggedIn = authState.isAuthenticated;
    const navigate = useNavigate();
    const location = useLocation();
    const [userData, setUserData] = useState(() => {
        try {
            const storedData = localStorage.getItem("userData");
            return storedData ? JSON.parse(storedData) : null;
        } catch (error) {
            console.error("Error parsing user data:", error);
            return null;
        }
    });
    const [lastAuthCheck, setLastAuthCheck] = useState(() => {
        return localStorage.getItem("lastAuthCheck") || "0";
    });

    const login = async (userData, customRedirect = null, forceReload = false) => {
        console.log('AuthContext: Starting login process', userData);
        try {
            console.log('AuthContext: Starting login process');

            // Validate user_type
            if (!userData.user_type || typeof userData.user_type !== 'string') {
                throw new Error('Invalid user type');
            }

            const safeUserData = {
                id: userData.id,
                email: userData.email,
                pass: userData.pass,
                user_type: userData.user_type,
                display_name: userData.display_name || userData.email
            };

            localStorage.setItem("userData", JSON.stringify(safeUserData));
            localStorage.setItem("lastAuthCheck", Date.now().toString());

            setUserData(safeUserData);
            setLastAuthCheck(Date.now().toString());
            setAuthState({
                isAuthenticated: true,
                isLoading: false,
                error: null
            });

            // If there's a return path in location state, navigate there
            // Otherwise, check localStorage for redirect_url (for automatic logout scenarios)
            const returnTo = customRedirect || location.state?.from || localStorage.getItem('redirect_url') || "/";

            // Clear the redirect_url from localStorage after using it
            if (localStorage.getItem('redirect_url')) {
                localStorage.removeItem('redirect_url');
            }

            if (forceReload) {
                window.location.href = returnTo;
                return true;
            }

            navigate(returnTo, { replace: true });

            return true;
        } catch (error) {
            console.error("AuthContext: Login error:", error);
            setAuthState({
                isAuthenticated: false,
                isLoading: false,
                error: "Login failed. Please try again."
            });
            return false;
        }
    };

    const logout = ({ redirect_url = false }) => {
        // Store current path for redirect after login
        if (redirect_url) {
            localStorage.setItem('redirect_url', location.pathname);
        }

        localStorage.removeItem('userData');
        localStorage.removeItem('presentuser');
        localStorage.removeItem('lastAuthCheck');
        setUserData(null);
        setLastAuthCheck("0");
        setAuthState({
            isAuthenticated: false,
            isLoading: false,
            error: null
        });
        // Navigate to home page instead of login to avoid automatic modal
        navigate("/", {
            state: {
                from: location.pathname,
                message: "You have been logged out successfully."
            }
        });
    };

    const checkAuth = async () => {
        try {
            if (!userData?.email || !userData?.id) {
                setAuthState({
                    isAuthenticated: false,
                    isLoading: false,
                    error: null
                });
                return;
            }

            const now = Date.now();
            const lastCheck = parseInt(lastAuthCheck);

            // Only check with server if more than 5 minutes have passed
            if (now - lastCheck < 5 * 60 * 1000) {
                setAuthState(prev => ({
                    ...prev,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null
                }));
                return;
            }

            const tableSchema = "";
            let tableName = "users";
            if (userData.user_type === "pharmacy") {
                tableName = "pharmacies";
            } else if (userData.user_type === "diagnostic") {
                tableName = "diagnostics";
            }
            const conditions = {
                id: userData.id,
                email: userData.email
            };
            const orderBy = "id ASC";

            const userResponse = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, "", "", "");

            if (userResponse.success && userResponse.data && userResponse.data.length > 0) {
                // Validate user_type from server response
                if (!userResponse.data[0].user_type || typeof userResponse.data[0].user_type !== 'string') {
                    throw new Error('Invalid user type received from server');
                }

                const safeUserData = {
                    id: userResponse.data[0].id,
                    email: userResponse.data[0].email,
                    pass: userResponse.data[0].pass,
                    user_type: userResponse.data[0].user_type,
                    display_name: userResponse.data[0].display_name || userResponse.data[0].email
                };
                localStorage.setItem("userData", JSON.stringify(safeUserData));
                localStorage.setItem("lastAuthCheck", now.toString());
                setUserData(safeUserData);
                setLastAuthCheck(now.toString());
                setAuthState({
                    isAuthenticated: true,
                    isLoading: false,
                    error: null
                });
            } else {
                throw new Error("User validation failed");
            }
        } catch (error) {
            console.error("Authentication error:", error);
            setAuthState({
                isAuthenticated: false,
                isLoading: false,
                error: "Session expired. Please login again."
            });
        }
    };

    // Check auth status on mount and when userData changes
    useEffect(() => {
        checkAuth();
    }, [userData]);

    // Handle session timeout
    useEffect(() => {
        let inactivityTimeout;

        const resetInactivityTimer = () => {
            clearTimeout(inactivityTimeout);
            inactivityTimeout = setTimeout(() => {
                if (isLoggedIn) {
                    logout({ redirect_url: true });
                }
            }, 60 * 60 * 1000); // 1 hour timeout
        };

        const handleUserActivity = () => {
            if (isLoggedIn) {
                resetInactivityTimer();
            }
        };

        if (isLoggedIn) {
            window.addEventListener("mousemove", handleUserActivity);
            window.addEventListener("keydown", handleUserActivity);
            window.addEventListener("scroll", handleUserActivity);
            window.addEventListener("click", handleUserActivity);
            resetInactivityTimer();
        }

        return () => {
            window.removeEventListener("mousemove", handleUserActivity);
            window.removeEventListener("keydown", handleUserActivity);
            window.removeEventListener("scroll", handleUserActivity);
            window.removeEventListener("click", handleUserActivity);
            clearTimeout(inactivityTimeout);
        };
    }, [isLoggedIn]);

    const value = {
        isLoggedIn,
        authState,
        userData,
        checkAuth,
        logout,
        login
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 
